import { useEffect, useMemo, useState } from 'react';
import { Phone } from 'lucide-react';
import Dialog from './Dialog';
import { EmptyState, ErrorState, LoadingState } from './Ui';
import { LsaCallActions, callRequest } from './LsaCalls';
import type { RecordData } from './Workflow';
import { callerLabel, callTimeLabel, phoneLabel } from '../lsa-call-format';
import './CallbackQueue.css';

const RECOVERY: Record<string, string> = { HIGH: 'High chance to win back', MEDIUM: 'Medium chance to win back' };
/** Digits for a tap-to-call link: "+1" plus ten digits for a US number, else the digits as given. */
export function dialable(value: unknown): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits.length >= 7 ? digits : null;
}

/**
 * Work the call-back list one call at a time (Shawn, 2026-10-30): the LSA calls the AI rated a
 * medium or high chance to win back that nobody has called back yet. Each card has the number
 * to tap, what happened on the call, the recording, and the same Called back tick and
 * follow-up log as the call details, then Next. `filters` are the calls list's own dates and
 * territory, so the queue holds the calls the "not called back" count shows.
 */
export default function CallbackQueue({ clientId, token, filters, onClose, onChanged }: {
  clientId: number; token?: string; filters: string; onClose: () => void; onChanged?: () => void;
}) {
  const [calls, setCalls] = useState<RecordData[] | null>(null);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState<Set<unknown>>(new Set());
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setCalls(null); setError('');
    callRequest(`/api/lsa/${clientId}/calls?show=not_called_back&page_size=200${filters ? `&${filters}` : ''}`, token)
      .then((r) => r.json()).then((data) => { if (!cancelled) setCalls(Array.isArray(data) ? data : data.calls || []); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); });
    return () => { cancelled = true; };
  }, [clientId, token, filters, attempt]);

  const call = calls?.[index] || null;
  const left = useMemo(() => (calls ? calls.filter((c) => !done.has(c.id)).length : 0), [calls, done]);
  function saved(updated: RecordData) {
    setCalls((rows) => rows?.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)) || null);
    setDone((d) => { const next = new Set(d); if (updated.office_follow_up) next.add(updated.id); else next.delete(updated.id); return next; });
    onChanged?.();
  }
  const total = calls?.length || 0;
  const shown = call;
  const phone = shown ? phoneLabel(shown.caller_phone) : null;
  const tel = shown ? dialable(shown.caller_phone) : null;
  const finished = !!calls && (total === 0 || index >= total);

  return (
    <Dialog title="Call-back list" description={calls && total ? `${left} of ${total} still to call back` : undefined} onClose={onClose}>
      {error ? <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />
        : !calls ? <LoadingState label="Loading the call-back list" />
          : finished ? (
            <EmptyState title={total ? 'You reached the end of the list' : 'Nobody is waiting for a call back'}
              description={total ? `${total - left} of ${total} marked called back. Calls you skipped stay on the list.` : 'Calls the AI rates a medium or high chance to win back show here until someone calls them back.'}
              action={total ? <button type="button" className="button secondary" onClick={() => setIndex(0)}>Start again</button> : undefined} />
          ) : shown && (
            <div className="cbq">
              <div className="cbq-head">
                <div className="cbq-who">
                  <strong>{shown.caller_name ? String(shown.caller_name) : callerLabel(shown)}</strong>
                  <span className="muted">{[callTimeLabel(shown), shown.territory ? String(shown.territory) : null].filter(Boolean).join(' · ')}</span>
                </div>
                {tel ? <a className="button primary cbq-call" href={`tel:${tel}`}><Phone aria-hidden="true" />Call {phone}</a>
                  : <span className="muted cbq-nophone">No phone number saved. Use Open in Google LSA.</span>}
              </div>
              <dl className="cbq-facts">
                {shown.recovery_probability ? <div><dt>Win-back chance</dt><dd>{RECOVERY[String(shown.recovery_probability).toUpperCase()] || String(shown.recovery_probability)}</dd></div> : null}
                {shown.diagnostic_text ? <div><dt>What happened</dt><dd>{String(shown.diagnostic_text)}</dd></div> : null}
                {shown.coaching_note ? <div><dt>What to say</dt><dd>{String(shown.coaching_note)}</dd></div> : null}
              </dl>
              <LsaCallActions key={String(shown.id)} call={shown} clientId={clientId} token={token} onSaved={saved} />
              <div className="cbq-nav">
                <span className="muted">Call {index + 1} of {total}</span>
                <div>
                  <button type="button" className="button secondary" disabled={index === 0} onClick={() => setIndex((i) => Math.max(0, i - 1))}>Back</button>
                  <button type="button" className="button primary" onClick={() => setIndex((i) => i + 1)}>{done.has(shown.id) ? 'Next call' : 'Skip for now'}</button>
                </div>
              </div>
            </div>
          )}
    </Dialog>
  );
}
